/**
 * MarkdownEditor - Modern WYSIWYG markdown editor powered by MDXEditor
 *
 * Replaces @uiw/react-md-editor with a Notion-like editing experience.
 * Supports headings, lists, links, tables, code blocks, images, and more.
 * Outputs standard markdown.
 *
 * The heavy MDXEditor implementation (~3 MB incl. CSS) lives in
 * ./MarkdownEditorImpl and is loaded via React.lazy, so importers (board,
 * meetings) only pull the editor bundle when an editor actually mounts.
 */

import { forwardRef, lazy, Suspense } from 'react';
import LoadingSpinner from './LoadingSpinner';

const MarkdownEditorImpl = lazy(() => import('./MarkdownEditorImpl'));

export interface MarkdownEditorProps {
  value: string;
  onChange?: (markdown: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  /** Show the toolbar (default: true) */
  toolbar?: boolean;
  /** Show diff/source toggle (default: true) */
  diffSource?: boolean;
  /** Minimum height in pixels */
  minHeight?: number;
  /** Image upload handler */
  onImageUpload?: (file: File) => Promise<string>;
  /** Dark mode */
  dark?: boolean;
}

export interface MarkdownEditorRef {
  getMarkdown: () => string;
  setMarkdown: (md: string) => void;
  focus: () => void;
}

const MarkdownEditor = forwardRef<MarkdownEditorRef, MarkdownEditorProps>((props, ref) => (
  <Suspense
    fallback={
      <div
        className="flex items-center justify-center py-8 text-neutral-400"
        style={props.minHeight ? { minHeight: props.minHeight } : undefined}
      >
        <LoadingSpinner size="sm" />
      </div>
    }
  >
    <MarkdownEditorImpl ref={ref} {...props} />
  </Suspense>
));

MarkdownEditor.displayName = 'MarkdownEditor';

export default MarkdownEditor;
